import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { openWhatsApp, PHONE_DISPLAY, EMAIL, ADDRESS } from "../lib/whatsapp";

const Section = ({ title, children }) => (
    <section className="mt-12">
        <h2 style={{fontFamily: "'Outfit', sans-serif"}} className="text-2xl md:text-3xl font-bold text-rvs-navy tracking-tight">
            {title}
        </h2>
        <div className="mt-5 space-y-4 text-[15px] md:text-base text-[#4A5568] leading-relaxed">
            {children}
        </div>
    </section>
);

export default function PrivacyPolicy() {
    return (
        <div data-testid="page-privacy" className="bg-rvs-cream">
            <article className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
                <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-rvs-navy/70 hover:text-rvs-orange link-underline">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Link>

                <p className="eyebrow mt-10">Legal · RVS Electronics</p>
                <h1 style={{fontFamily: "'Outfit', sans-serif"}} className="mt-4 text-5xl sm:text-6xl font-black tracking-tighter leading-[1.02] text-rvs-navy">
                    Privacy Policy
                </h1>
                <p className="mt-4 text-sm uppercase tracking-[0.2em] text-rvs-orange font-bold">
                    Effective Date: 10 May 2026
                </p>

                <p className="mt-10 text-base md:text-lg text-[#4A5568] leading-relaxed">
                    At RVS Electronics, we value the privacy of our customers, retailers, and website visitors. This Privacy Policy explains how we collect, use, and protect information when you visit our website or contact us for services.
                </p>

                <Section title="About Us">
                    <p>
                        RVS Electronics is an authorized distributor of Sun Direct operating from Armoor, Nizamabad District, Telangana.
                    </p>
                    <ul className="space-y-1.5">
                        <li><span className="font-semibold text-rvs-navy">Business Address:</span> {ADDRESS}</li>
                        <li><span className="font-semibold text-rvs-navy">Phone / WhatsApp:</span> {PHONE_DISPLAY}</li>
                        <li><span className="font-semibold text-rvs-navy">Email:</span> {EMAIL}</li>
                    </ul>
                </Section>

                <Section title="Information We Collect">
                    <p>We may collect information that you voluntarily provide to us, including:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>name</li>
                        <li>mobile number</li>
                        <li>email address</li>
                        <li>address or service location</li>
                        <li>enquiry details</li>
                        <li>retailer-related business communication details</li>
                    </ul>
                    <p>
                        We may also collect limited technical information such as browser type, device type, IP address, and pages visited for website administration and performance improvement.
                    </p>
                </Section>

                <Section title="How We Use Your Information">
                    <p>We use the information collected for legitimate business purposes, including:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>responding to enquiries</li>
                        <li>providing customer support</li>
                        <li>processing service requests</li>
                        <li>coordinating installation and technical service visits</li>
                        <li>retailer support and operational communication</li>
                        <li>recharge-related communication</li>
                        <li>informing customers about subscription validity, renewal reminders, and service-related updates</li>
                        <li>improving our services and website performance</li>
                    </ul>
                </Section>

                <Section title="WhatsApp Communication">
                    <p>When you contact RVS Electronics through WhatsApp, phone call, or website enquiry forms, we may use your contact details to communicate regarding:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>service requests</li>
                        <li>installation coordination</li>
                        <li>recharge assistance</li>
                        <li>retailer operational support</li>
                        <li>customer service updates</li>
                    </ul>
                    <p>We do not use customer information for unlawful or unrelated marketing activities.</p>
                </Section>

                <Section title="Information Sharing">
                    <p>RVS Electronics does not sell or rent personal information.</p>
                    <p>Information may be shared only where reasonably necessary for business operations, such as with:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>field technicians for service coordination</li>
                        <li>internal staff for support and operational management</li>
                        <li>authorized business partners where required for service fulfillment</li>
                        <li>regulatory or legal authorities where required by law</li>
                    </ul>
                </Section>

                <Section title="Data Security">
                    <p>We take reasonable administrative and operational measures to protect information from unauthorized access, misuse, or disclosure.</p>
                    <p>However, no internet-based transmission or electronic storage can be guaranteed to be completely secure.</p>
                </Section>

                <Section title="Third-Party Services">
                    <p>Our website may contain references or links to third-party services, including service providers, payment services, or future digital platforms.</p>
                    <p>RVS Electronics is not responsible for the privacy practices or content of third-party websites.</p>
                </Section>

                <Section title="Data Retention">
                    <p>We may retain business-related information only for as long as reasonably necessary for customer support, retailer operations, service history, legal compliance, and internal business records.</p>
                </Section>

                <Section title="Your Choices">
                    <p>You may contact us at any time to:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>request correction of your information</li>
                        <li>request removal of contact information from promotional communication where applicable</li>
                        <li>ask questions regarding this Privacy Policy</li>
                    </ul>
                </Section>

                <Section title="Contact Us">
                    <div className="bg-white border border-rvs-navy-10 rounded-2xl p-6 md:p-8">
                        <p className="font-bold text-rvs-navy text-lg">RVS Electronics</p>
                        <p className="mt-2">{ADDRESS}</p>
                        <p className="mt-1"><span className="font-semibold text-rvs-navy">Phone / WhatsApp:</span> {PHONE_DISPLAY}</p>
                        <p className="mt-1"><span className="font-semibold text-rvs-navy">Email:</span> {EMAIL}</p>
                        <button
                            onClick={() => openWhatsApp("Hello RVS Electronics, I have a question about your Privacy Policy.")}
                            data-testid="privacy-whatsapp-btn"
                            className="mt-6 btn-orange inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
                        >
                            <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
                        </button>
                    </div>
                </Section>
            </article>
        </div>
    );
}
